import {I18n} from '@aofl/i18n';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';
import translations from './i18n';
import {storeInstance} from '@aofl/store/dist/legacy';
import {
  sdoNs,
  jobStatuses,
  jobTypes,
  rosterJobStages,
  rosterErrors,
  exportJobStages,
  inviteJobStages,
  bulkRevokeStages,
  qrJobStages,
  productStatuses,
  teacherAccessStatuses,
  licenseHomeAccessTypes,
  grades,
  products as productConst,
  abcSubjectBuckets,
  activationStartType,
  localStorageKeys, picturePasscodeResetJobStages, printReportLabels,
} from './constants-enumerate';
import './session-sdo';
import {wrapUnsafeHtml} from '@modules/wrap-unsafe-html';

const i18n = new I18n(translations);
i18n.lang = localStorage.getItem(localStorageKeys.LANG);
storeInstance.subscribe(() => {
  const state = storeInstance.getState();
  i18n.lang = state[sdoNs.SESSION]?.locale || localStorage.getItem(localStorageKeys.LANG);
});

const tierStructure = {
  get CLASSES() {
    return (async () => unsafeHTML(await i18n.__('<tt-nwlZSDjC>', 'Classes')))();
  },
  get CLASS() {
    return i18n.__('<tt-lM5Rt3fQ>', 'Class');
  }
};

const licenseHomeAccessTypeLabels = {
  get [licenseHomeAccessTypes.FULL]() {
    return wrapUnsafeHtml(i18n.__('<tt-dNJkl+eA>', 'Full-Access'));
  },
  get [licenseHomeAccessTypes.LIMITED]() {
    return wrapUnsafeHtml(i18n.__('<tt-TSURyYZf>', 'Limited-Access'));
  },
  get [licenseHomeAccessTypes.NONE]() {
    return wrapUnsafeHtml(i18n.__('<tt-E1SJluMo>', 'None'));
  },
};

const activationStartTypeLabels = {
  get [activationStartType.ON_LICENSE]() {
    return wrapUnsafeHtml(i18n.__('<tt-aq6f4kri>', 'Start on Assignment'));
  },
  get [activationStartType.ON_REDEEM]() {
    return wrapUnsafeHtml(i18n.__('<tt-TFVrIVF+>', 'Start on Redeem'));
  },
};

const chartTitles = {
  get USAGE_ACTIVE_STUDENTS_WEEKLY() {
    return wrapUnsafeHtml(i18n.__('<tt-6ITUrWDv>', 'Line Graph of Weekly Active Students by Product.'));
  },
  get USAGE_ACTIVE_STUDENTS_MONTHLY() {
    return wrapUnsafeHtml(i18n.__('<tt-xNNY/Nfs>', 'Line Graph of Monthly Active Students by Product.'));
  },
  get USAGE_AVERAGE_MINUTES_WEEKLY() {
    return wrapUnsafeHtml(i18n.__('<tt-4sxGtEam>', 'Line Graph of Weekly Average Minutes Per Student by Product.'));
  },
  get USAGE_AVERAGE_MINUTES_MONTHLY() {
    return wrapUnsafeHtml(i18n.__('<tt-2zpWk5Mv>', 'Line Graph of Monthly Average Minutes Per Student by Product.'));
  },
  get USAGE_AVERAGE_TEACHERS_WEEKLY() {
    return wrapUnsafeHtml(i18n.__('<tt-Hg6Y/kzU>', 'Line Graph of Weekly Active Teachers by Product.'));
  },
  get USAGE_AVERAGE_TEACHERS_MONTHLY() {
    return wrapUnsafeHtml(i18n.__('<tt-2LMWdcS0>', 'Line Graph of Monthly Active Teachers by Product.'));
  },
};

const leftMenuLabels = {
  get REDEEM_CODE() {
    return wrapUnsafeHtml(i18n.__('<tt-hVmpNfEG>', 'Redeem Code'));
  },
  get PRODUCT_LAUNCH() {
    return wrapUnsafeHtml(i18n.__('<tt-nDoIXRmW>', 'Product Launch'));
  },
  get TEACHERS() {
    return (async () => unsafeHTML(await i18n.__('<tt-u8553hwo>', 'Teachers')))();
  },
  get CLASSES() {
    return (async () => unsafeHTML(await i18n.__('<tt-QSS2L0Ox>', 'Classes')))();
  },
  get STUDENTS() {
    return (async () => unsafeHTML(await i18n.__('<tt-XeARR41L>', 'Students')))();
  },
  get ROSTERS() {
    return (async () => unsafeHTML(await i18n.__('<tt-vqGLSDxt>', 'Rosters')))();
  },
  get LICENSES() {
    return (async () => unsafeHTML(await i18n.__('<tt-InnYLcPy>', 'Licenses')))();
  },
  get MANAGEMENT() {
    return (async () => unsafeHTML(await i18n.__('<tt-a4AjjI+J>', 'Management')))();
  },
  get HISTORY() {
    return (async () => unsafeHTML(await generalLabels.history))();
  },
  get RESOURCES() {
    return (async () => unsafeHTML(await i18n.__('<tt-bqsfCGi7>', 'Resources')))();
  },
  get MANAGE_ACCOUNT_AND_NOTIFICATIONS() {
    return wrapUnsafeHtml(i18n.__('<tt-KK3OwD5T>', 'Manage Account & Notifications'));
  },
};

const dashboardAriaLabels = {
  progressing(value) {
    return i18n._r(i18n.__('<tt-W9Lb1+On>', '%r1%% Students Progressing'), value);
  },
  struggling(value) {
    return i18n._r(i18n.__('<tt-d65NMsoq>', '%r1%% Students that need support\n'), value);
  },
  inactive(value) {
    return i18n._r(i18n.__('<tt-2bDNwtSE>', '%r1%% Students Inactive'), value);
  },
  minutes45(value) {
    return i18n._r(i18n.__('<tt-1Xjjkacl>', '%r1%% Students with 45+ Minutes'), value);
  },
  minutes31(value) {
    return i18n._r(i18n.__('<tt-coDpUSo0>', '%r1%% Students with 31 - 45 Minutes'), value);
  },
  minutes16(value) {
    return i18n._r(i18n.__('<tt-qobeIqhH>', '%r1%% Students with 16 - 30 Minutes'), value);
  },
  minutes1(value) {
    return i18n._r(i18n.__('<tt-DLnNymNA>', '%r1%% Students with 1 - 15 Minutes'), value);
  },
  minutes60(value) {
    return i18n._r(i18n.__('<tt-cEBZPSSm>', '%r1%% Students with 60+ Minutes'), value);
  },
  minutes41(value) {
    return i18n._r(i18n.__('<tt-7YYtobkm>', '%r1%% Students with 41 - 59 Minutes'), value);
  },
  minutes21(value) {
    return i18n._r(i18n.__('<tt-W7FCk5GO>', '%r1%% Students with 21 - 40 Minutes'), value);
  },
  minutes1to20(value) {
    return i18n._r(i18n.__('<tt-evn+zfAH>', '%r1%% Students with 1 - 20 Minutes'), value);
  },
  high(value) {
    return i18n._r(i18n.__('<tt-eUJU5Nta>', '%r1%% Students Completed 25+ activities'), value);
  },
  med(value) {
    return i18n._r(i18n.__('<tt-bv9EqtoP>', '%r1%% Students Completed 1 - 24 activities'), value);
  },
  progressingNum(value) {
    return i18n._r(i18n.__('<tt-E07776Hn>', '%r1% Students Progressing'), value);
  },
  strugglingNum(value) {
    return i18n._r(i18n.__('<tt-YlBo2cDa>', '%r1% Students that need support'), value);
  },
  inactiveNum(value) {
    return i18n._r(i18n.__('<tt-9Gr0KyvT>', '%r1% Students Inactive'), value);
  },
  minutes45Num(value) {
    return i18n._r(i18n.__('<tt-B41jHk/X>', '%r1% Students with 45+ Minutes'), value);
  },
  minutes31Num(value) {
    return i18n._r(i18n.__('<tt-XyRJ1lDH>', '%r1% Students with 30 - 44 Minutes'), value);
  },
  minutes16Num(value) {
    return i18n._r(i18n.__('<tt-U2umbtMS>', '%r1% Students with 15 - 29 Minutes'), value);
  },
  minutes1Num(value) {
    return i18n._r(i18n.__('<tt-6GjxDpvO>', '%r1% Students with 1 - 14 Minutes'), value);
  },
  minutes60Num(value) {
    return i18n._r(i18n.__('<tt-LxQUB03o>', '%r1% Students with 60+ Minutes'), value);
  },
  minutes41Num(value) {
    return i18n._r(i18n.__('<tt-JRnm809y>', '%r1% Students with 40 - 59 Minutes'), value);
  },
  minutes21Num(value) {
    return i18n._r(i18n.__('<tt-Rk6Wm9FS>', '%r1% Students with 20 - 39 Minutes'), value);
  },
  minutes1to20Num(value) {
    return i18n._r(i18n.__('<tt-7GEa/WKr>', '%r1% Students with 1 - 19 Minutes'), value);
  },
  highNum(value) {
    return i18n._r(i18n.__('<tt-h/ujDgUe>', '%r1% Students Completed 25+ activities'), value);
  },
  medNum(value) {
    return i18n._r(i18n.__('<tt-ObAWbmZX>', '%r1% Students Completed 1 - 24 activities'), value);
  },
  get STUDENTS() {
    return i18n.__('<tt-uTtt+mdU>', 'Students');
  },
  get MINUTES_60() {
    return i18n.__('<tt-i5rQzH/A>', '60+ Minutes');
  },
  get MINUTES_45() {
    return i18n.__('<tt-AZ6ZRcxw>', '45+ Minutes');
  },
  get MINUTES_41() {
    return i18n.__('<tt-9DWEI/Bq>', '40 - 59 Minutes');
  },
  get MINUTES_31() {
    return i18n.__('<tt-b2fHBaEm>', '30 - 44 Minutes');
  },
  get MINUTES_21() {
    return i18n.__('<tt-MFiNBFPA>', '20 - 39 Minutes');
  },
  get MINUTES_16() {
    return i18n.__('<tt-K4NzcaOu>', '15 - 29 Minutes');
  },
  get MINUTES_1_TO_20() {
    return i18n.__('<tt-c9IJ3OUO>', '1 - 19 Minutes');
  },
  get MINUTES_1() {
    return i18n.__('<tt-66e9yHQD>', '1 - 14 Minutes');
  },
  get BELOW_TARGET() {
    return i18n.__('<tt-g/Sg4qiB>', 'Completed 1 - 24 activities');
  },
  get HIT_TARGET() {
    return i18n.__('<tt-Pi9cRTRY>', 'Completed 25+ activities');
  },
  get INACTIVE_TARGET() {
    return i18n.__('<tt-hC2YyKCM>', 'Inactive');
  },
  get PROGRESSING() {
    return i18n.__('<tt-ff8nrF39>', 'Progressing');
  },
  get STRUGGLING() {
    return i18n.__('<tt-OM4b8SZa>', 'Needs Support');
  },
  get PREVIOUS_WEEKS() {
    return i18n.__('<tt-FCNVasXN>', 'View Previous Weeks');
  },
  get PREVIOUS_MONTHS() {
    return i18n.__('<tt-BarHMA99>', 'View Previous Months');
  },
  get NEXT_WEEKS() {
    return i18n.__('<tt-Kr+rWgpr>', 'View Next Weeks');
  },
  get NEXT_MONTHS() {
    return i18n.__('<tt-YgRxzQaL>', 'View Next Months');
  },
  get MINUTES() {
    return i18n.__('<tt-0gbxEBl7>', 'minutes');
  },
};

const conceptsMasteredColumnLabels = {
  get objective() {
    return wrapUnsafeHtml(i18n.__('<tt-gfz+f33l>', 'Skills and Concepts'));
  },
  get total() {
    return wrapUnsafeHtml(i18n.__('<tt-rJQFzh71>', 'All'));
  },
};

const studentDashboardTimeSpentAriaLabels = {
  get [abcSubjectBuckets.MATH]() {
    return i18n.__('<tt-IURe51D7>', 'Minutes spent on Math');
  },
  get [abcSubjectBuckets.ARTS_AND_SCIENCES]() {
    return i18n.__('<tt-dwf20EOu>', 'Minutes spent on Arts & Sciences');
  },
  get [abcSubjectBuckets.FREE_PLAY]() {
    return i18n.__('<tt-axrTdsky>', 'Minutes spent on Free Play');
  },
  get [abcSubjectBuckets.READING]() {
    return i18n.__('<tt-NrjUMwKx>', 'Minutes spent on Reading');
  },
};

const studentDashboardMinutesRemainingAriaLabels = {
  get [abcSubjectBuckets.MATH]() {
    return i18n.__('<tt-njKxn7Xm>', 'Minutes remaining on Math');
  },
  get [abcSubjectBuckets.ARTS_AND_SCIENCES]() {
    return i18n.__('<tt-8QVB+DA5>', 'Minutes remaining on Arts & Sciences');
  },
  get [abcSubjectBuckets.FREE_PLAY]() {
    return i18n.__('<tt-c7vdFTE8>', 'Minutes remaining on Free Play');
  },
  get [abcSubjectBuckets.READING]() {
    return i18n.__('<tt-LH/pCqkQ>', 'Minutes remaining on Reading');
  },
};

const studentDashboardAriaLabels = {
  get minutesLeftThisWeek() {
    return i18n.__('<tt-KWyyIIqa>', 'Minutes left this week');
  },
  get minutesSpentThisWeek() {
    return i18n.__('<tt-x2i1aCFv>', 'Minutes spent this week');
  },
};

const dashboardLabels = {
  get WEEKLY_REPORT() {
    return wrapUnsafeHtml(i18n.__('<tt-MxfCmcYN>', 'Weekly Report'));
  },
  get ACTIVITY() {
    return wrapUnsafeHtml(i18n.__('<tt-qwGy/4y8>', 'Activity'));
  },
  get STUDENTS() {
    return wrapUnsafeHtml(i18n.__('<tt-VAEN+XKn>', 'Students'));
  },
  get USAGE() {
    return wrapUnsafeHtml(i18n.__('<tt-6hZCjTjd>', 'Usage'));
  },
  get DOWNLOAD_CSV() {
    return wrapUnsafeHtml(i18n.__('<tt-/QJoCmNS>', 'Download CSV'));
  },
  get DOWNLOAD_CLASSROOM_SUMMARY_AGG() {
    return wrapUnsafeHtml(i18n.__('<tt-EvL5n1GV>', 'Classroom Summary'));
  },
  get RECOMMENDED_ACTIVITIES() {
    return wrapUnsafeHtml(i18n.__('<tt-FwpFdy2i>', 'Recommended Activities'));
  },
  get NO_STUDENTS_IN_THIS_FILTER() {
    return wrapUnsafeHtml(i18n.__('<tt-HBLgvfhN>', 'No students in this filter'));
  },
  get ACTIVE_STUDENTS() {
    return wrapUnsafeHtml(i18n.__('<tt-B9j1t5qN>', 'Active Students'));
  },
  get TIME_SPENT_SUBTITLE() {
    return wrapUnsafeHtml(i18n.__('<tt-7NrrNHl7>', 'Minutes students spent on learning activities this week. Excludes any time outside of a lesson.'));
  },
  get WEEKLY_PROGRESS_SUBTITLE() {
    return wrapUnsafeHtml(i18n.__('<tt-/bEp3XKQ>', 'Breaks down students progressing from those who need support on activities this week, and anyone inactive'));
  },
  get RECOMMENDED_ACTIVITIES_SUBTITLE() {
    return wrapUnsafeHtml(i18n.__('<tt-mCKpMemB>', 'Skills/Concepts that most students are working on, based on overall progress so far. Skills/Concepts with "Needs Support" counts are prioritized'));
  },
  get SKILLS_DURING_WEEK() {
    return wrapUnsafeHtml(i18n.__('<tt-udEYW1xm>', 'Skills During Selected Week'));
  },
  get NO_USAGE_FROM_STUDENTS() {
    return wrapUnsafeHtml(i18n.__('<tt-1poNTlx/>', 'No usage data from students yet, please check again later'));
  },
  get RELATED() {
    return wrapUnsafeHtml(i18n.__('<tt-/ZteUCWs>', 'Related'));
  },
  get THIS_WEEK() {
    return wrapUnsafeHtml(i18n.__('<tt-pRBhEvGE>', 'This Week'));
  },
  get HISTORIC() {
    return wrapUnsafeHtml(i18n.__('<tt-mwEG+zEZ>', 'Historic'));
  },
  get HISTORY() {
    return wrapUnsafeHtml(generalLabels.history);
  },
  get BY_WEEK() {
    return wrapUnsafeHtml(i18n.__('<tt-l6V2aQVv>', 'By Week'));
  },
  get BY_MONTH() {
    return wrapUnsafeHtml(i18n.__('<tt-OAnsA8Z1>', 'By Month'));
  },
  get OVERALL() {
    return wrapUnsafeHtml(i18n.__('<tt-anO7EUog>', 'Overall'));
  },
  get OVERALL_PROGRESS() {
    return wrapUnsafeHtml(i18n.__('<tt-yd4O4aEz>', 'Overall Progress'));
  },
  studentGroupsOnConcept(count) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-gL8RSHc6>', 'Students on This Skill/Concept (%r1%)'), count));
  },
  rangeLabel(duration) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-U6RKbyuB>', '%r1% mins'), duration));
  },
  strugglingNeedsSupportCount(count) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-ouIiyKIz>', '<span class="font-weight-bold">Needs More Support (%r1%)</span><span class="font-weight-semibold"> - Needs More Support</span>'), count), true);
  },
  progressingCurrentlyWorkingOnCount(count) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-plgnHF1z>', '<span class="font-weight-bold">In Progress (%r1%)</span><span class="font-weight-semibold"> - Currently Working On</span>'), count), true);
  },
  masteredWithinProgramCount(count) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-FPEZTECz>', '<span class="font-weight-bold">Completed (%r1%)</span><span class="font-weight-semibold"> - Mastered Within Program</span>'), count), true);
  },
  creditedWithinProgramCount(count) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-vjIbzBsa>', '<span class="font-weight-bold">Credited (%r1%)</span><span class="font-weight-semibold"> - Inferred Learning</span>'), count), true);
  },
  priorKnowledgePreviouslyMasteredCount(count) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-zKl+oRmp>', '<span class="font-weight-bold">Prior Knowledge (%r1%)</span><span class="font-weight-semibold"> - Previously Mastered</span>'), count), true);
  },
  inactiveUpNextInLPCount(count) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-huEub0gj>', '<span class="font-weight-bold">Inactive (%r1%)</span><span class="font-weight-semibold"> - Coming Soon in Their Learning Path</span>'), count), true);
  },
  timeSpentOnSkills(timeframe) {
    const map = {
      thisWeek: dashboardLabels.THIS_WEEK,
      byWeek: dashboardLabels.BY_WEEK,
      byMonth: dashboardLabels.BY_MONTH,
      overall: dashboardLabels.OVERALL
    };
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-QCp9dhX7>', 'Time Spent on Skills %r1%'), map[timeframe]));
  },
  timeSpentInGroupName(groupName) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-4KzIAePE>', 'Time Spent in %r1%'), groupName));
  },
  progressInGroupName(groupName) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-0gwuhVvc>', 'Progress in %r1%'), groupName));
  },
  groupNotActiveInWeek(groupName) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-+VWfG1aL>', '%r1% does not exist for selected week'), groupName));
  },
  get SKILLS_AND_CONCEPTS() {
    return wrapUnsafeHtml(i18n.__('<tt-M1xPkrkl>', 'Skills and Concepts'));
  },
  get SNAPSHOT() {
    return wrapUnsafeHtml(i18n.__('<tt-DxBNEUCh>', 'Weekly Snapshot'));
  },
  get USAGE_TRACKER() {
    return wrapUnsafeHtml(i18n.__('<tt-cgnCiAqi>', 'Usage Tracker'));
  },
  get CONDITIONAL_STUDENTS_SHOWING_CLASS_LABEL() {
    return wrapUnsafeHtml(i18n.__('<tt-QABWgrlQ>', 'Due to how weekly usage data is aggregated, removed/unlicensed students will still be shown until the following week'));
  },
  get CONDITIONAL_STUDENTS_SHOWING_GROUPS() {
    return wrapUnsafeHtml(i18n.__('<tt-sF+NB105>', '* Some students were removed or unlicensed this week. Due to how weekly usage data is aggregated, they will still be shown until the following week.'));
  },
  get CONDITIONAL_REMOVED_GROUPS() {
    return wrapUnsafeHtml(i18n.__('<tt-ReojatWO>', '** Some groups (such as schools or classes) were  deleted from the roster. Due to how weekly usage data is aggregated, they will still be shown until the following week'));
  },
  get TOTAL_STUDENTS() {
    return wrapUnsafeHtml(i18n.__('<tt-VJEMc33L>', 'Total<br/>Students'), true);
  },
  get TOTAL_STUDENTS_NO_BREAK() {
    return wrapUnsafeHtml(i18n.__('<tt-9R/NylnN>', 'Total Students'));
  },
  get PRIOR_KNOWLEDGE() {
    return wrapUnsafeHtml(i18n.__('<tt-/FV74/l3>', 'Prior Knowledge'));
  },
  get CREDITED() {
    return wrapUnsafeHtml(i18n.__('<tt-GKd/KupV>', 'Credited'));
  },
  get IN_PROGRESS() {
    return wrapUnsafeHtml(i18n.__('<tt-n6eO0nSH>', 'In Progress'));
  },
  get NOT_STARTED() {
    return wrapUnsafeHtml(i18n.__('<tt-agv+l1Ra>', 'Not Started'));
  },
  get PROGRESSING() {
    return wrapUnsafeHtml(i18n.__('<tt-CKqR4UMm>', 'Progressing'));
  },
  get STRUGGLING() {
    return wrapUnsafeHtml(i18n.__('<tt-YZzwU5ni>', 'Needs Support'));
  },
  get FUTURE_SKILLS() {
    return wrapUnsafeHtml(i18n.__('<tt-Hjl9mzRF>', 'Future Skills'));
  },
  get INACTIVE() {
    return wrapUnsafeHtml(i18n.__('<tt-itLOQyh3>', 'Inactive'));
  },
  get COMPLETED() {
    return wrapUnsafeHtml(i18n.__('<tt-8Hi6ayZq>', 'Completed'));
  },
  get NOT_ON_TRACK() {
    return wrapUnsafeHtml(i18n.__('<tt-UAwHX0b4>', 'Not On Track'));
  },
  get ON_TRACK() {
    return wrapUnsafeHtml(i18n.__('<tt-UzO2a/l+>', 'On Track'));
  },
  get MET_GOAL() {
    return wrapUnsafeHtml(i18n.__('<tt-qJS4eLy8>', 'Met Goal'));
  },
  get MASTERED() {
    return wrapUnsafeHtml(i18n.__('<tt-OZsM6jc2>', 'Mastered'));
  },
  get MINUTES_60() {
    return wrapUnsafeHtml(i18n.__('<tt-h16UipvI>', '60+ Minutes'));
  },
  get MINUTES_45() {
    return wrapUnsafeHtml(i18n.__('<tt-2ErRaaaR>', '45+ Minutes'));
  },
  get MINUTES_41() {
    return wrapUnsafeHtml(i18n.__('<tt-YMBpBJKj>', '40 - 59 Minutes'));
  },
  get MINUTES_31() {
    return wrapUnsafeHtml(i18n.__('<tt-PMse0s0H>', '30 - 44 Minutes'));
  },
  get MINUTES_21() {
    return wrapUnsafeHtml(i18n.__('<tt-+jtXCr1b>', '20 - 39 Minutes'));
  },
  get MINUTES_16() {
    return wrapUnsafeHtml(i18n.__('<tt-MjRyuCdP>', '15 - 29 Minutes'));
  },
  get MINUTES_1_TO_20() {
    return wrapUnsafeHtml(i18n.__('<tt-IYL9HwUI>', '1 - 19 Minutes'));
  },
  get MINUTES_1() {
    return wrapUnsafeHtml(i18n.__('<tt-3JiyPQ9w>', '1 - 14 Minutes'));
  },
  get ACTIVITY_TIME_PER_STUDENT() {
    return wrapUnsafeHtml(i18n.__('<tt-nJxf88mb>', 'Minutes Per<br/>Active Student'), true);
  },
  get BELOW_TARGET() {
    return wrapUnsafeHtml(i18n.__('<tt-GZaAvX1Q>', 'Completed 1 - 24 activities'));
  },
  get HIT_TARGET() {
    return wrapUnsafeHtml(i18n.__('<tt-hrp0W89k>', 'Completed 25+ activities'));
  },
  get MINUTES() {
    return wrapUnsafeHtml(i18n.__('<tt-EAARqseS>', 'minutes'));
  },
  get LESSON() {
    return (async () => unsafeHTML(await i18n.__('<tt-qZjJHe5r>', 'lesson')))();
  },
  get LESSONS() {
    return (async () => unsafeHTML(await i18n.__('<tt-1p3UP0bW>', 'lessons')))();
  },
  get HIDE() {
    return wrapUnsafeHtml(i18n.__('<tt-iHF7fb1y>', 'Hide'));
  },
  get SELECT_A_STATUS() {
    return wrapUnsafeHtml(i18n.__('<tt-9Tn9OfE7>', 'Select a Status'));
  },
  get SELECT_A_GRADE() {
    return wrapUnsafeHtml(i18n.__('<tt-SdRGhDxo>', 'Select a Grade'));
  },
  get SELECT_A_STANDARD_SET() {
    return wrapUnsafeHtml(i18n.__('<tt-FWdSL1aN>', 'Select a Standard Set'));
  },
  get NO_LICENSED_STUDENTS_FOUND() {
    return wrapUnsafeHtml(i18n.__('<tt-o4UfwiX4>', 'No licensed students found. Please check back later when students are licensed in the product.'));
  },
  getNumberOfStudents(totalStudents, groupName) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-0WE4vKdL>', '# of Students with progress (out of %r1% total in <strong>%r2%</strong>)'), totalStudents, groupName), true);
  },
  get OK_BUTTON() {
    return wrapUnsafeHtml(i18n.__('<tt-MbFAcd2M>', 'OK'));
  },
  get ACTION_ITEMS() {
    return wrapUnsafeHtml(i18n.__('<tt-2jMKxfLZ>', 'Action Items This Week'));
  },
  get ACTION_ITEMS_SUBTITLE() {
    return wrapUnsafeHtml(i18n.__('<tt-Kuv38HIm>', 'Items to pay attention to based on your students\' progress this week.'));
  },
  getActionItemWeeklyTargetMet(name, goal) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-X89Haks/>', '%r1% reached the %r2% minute target this week! Give %r1% a high five!'), name, goal));
  },
  getTeacherDashboardActionItemWeeklyTargetMet(count, goal) {
    return wrapUnsafeHtml(
      i18n._r(
        i18n._c('<tt-JsE5LuVe>', '%c1% reached the %r2% minute target this week! %c2%',
          {'1': '%r1% student', '%other%': '%r1% students'},
          count,
          {'1': 'High five!', '%other%': 'Give them a high five!'},
          count
        ),
        count,
        goal
      ));
  },
  getActionItemWeeklyTargetProgressing(name, goal) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-8im2kJhV>', '%r1% is progressing toward the %r2% minute target this week. Keep it up!'), name, goal));
  },
  getTeacherDashboardActionItemWeeklyTargetProgressing(count, goal) {
    return wrapUnsafeHtml(
      i18n._r(
        i18n._c('<tt-ev0Xqtwt>', '%c1% progressing towards the %r2% minute target this week. Keep it up!',
          {'1': '%r1% student is', '%other%': '%r1% students are'},
          count
        ),
        count,
        goal
      ));
  },
  getActionItemWeeklyTargetNotStarted(name) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-giMQdYfs>', '%r1% hasn\'t made any progress this week and may need a gentle nudge!'), name));
  },
  getTeacherDashboardActionItemWeeklyTargetNotStarted(count) {
    return wrapUnsafeHtml(
      i18n._r(
        i18n._c('<tt-adTpQ7R9>', '%c1% made any progress this week %c2% and may need a gentle nudge!',
          {'1': '%r1% student hasn\'t', '%other%': '%r1% students haven\'t'},
          count,
          {'1': '', '%other%': 'yet'},
          count
        ),
        count,
      ));
  },
  getActionItemWeeklyStruggling(name) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-eMK6i79p>', '%r1% Needs Support on a skill and could use a check-in.'), name));
  },
  getTeacherDashboardActionItemWeeklyStruggling(count) {
    return wrapUnsafeHtml(
      i18n._r(
        i18n._c('<tt-gNa+u486>', '%c1% support this week and could use a check-in.',
          {'1': '%r1% student needs', '%other%': '%r1% students need'},
          count
        ),
        count,
      ));
  },
  get NUMBER_OF_STUDENTS() {
    return wrapUnsafeHtml(i18n.__('<tt-iy6Jly7i>', 'Number of Students:'));
  },
  get TIME_SPENT() {
    return wrapUnsafeHtml(i18n.__('<tt-bkh5c8/Y>', 'Time Spent'));
  },
  get TIME_REMAINING() {
    return wrapUnsafeHtml(i18n.__('<tt-RahG+zHR>', 'Time Remaining'));
  },
  get ACTIVE_THIS_WEEK() {
    return wrapUnsafeHtml(i18n.__('<tt-jYDJuE26>', 'Active This Week'));
  },
  get NO_MATCHING_SKILLS_OR_CONCEPTS() {
    return wrapUnsafeHtml(i18n.__('<tt-0I76TGDt>', 'No skills or concepts matching your current filters.'));
  },
  selectCategory(category) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-wOBLN7U+>', 'Select %r1%'), category));
  },
  get NO_ACTIVE_STUDENTS_FOUND() {
    return wrapUnsafeHtml(i18n.__('<tt-yTPAWQbX>', 'No active students found. Please check back later when students are active in the product.'));
  },
  get WEEK_OF() {
    return wrapUnsafeHtml(i18n.__('<tt-SR4+hx0s>', 'Week of'));
  },
  get PERCENT_ROSTERED_ACTIVE_STUDENTS() {
    return wrapUnsafeHtml(i18n.__('<tt-WvESKx2d>', '% Of Rostered Students Who Are Active Per Week'));
  },
  get PERCENT_ACTIVE_STUDENTS() {
    return wrapUnsafeHtml(i18n.__('<tt-rXuANMEK>', '% Of Active Students By Weekly Time Spent'));
  },
  get PLEASE_SELECT_GRADE() {
    return wrapUnsafeHtml(i18n.__('<tt-qcV/amwr>', 'Please select a grade from the filter to see the usage progress.'));
  },
  get REPORT_AVAILABLE() {
    return wrapUnsafeHtml(i18n.__('<tt-kbum5s+T>', 'See Report'));
  },
  recommendedActivityName(activity) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-B/LH6J+f>', '%r1%'), activity));
  },
  get INSTRUCTION_TYPES() {
    return wrapUnsafeHtml(i18n.__('<tt-UzXlx7ng>', 'Instruction Types'));
  },
  estimatedTime(time) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-2WmnINUI>', 'Estimated Time: %r1% mins.'), time));
  },
};

const studentDashboardLabels = {
  matchingSkillsWithProgressByGrade(progress) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-0n4yC1Gy>', 'Matching skills/concepts with progress: %r1%'), progress));
  },
  totalSkillsWithProgressByGrade(progress) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-KpaA/go+>', 'Total skills/concepts with progress: %r1%'), progress));
  },
  get clickOnASkillToLearnMore() {
    return wrapUnsafeHtml(i18n.__('<tt-4Fu4gQaP>', 'Click on a skill to learn more.'));
  },
  skillsAndConceptsCompletedByStatus(color, status, progress) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-MY7QmYb0>', '<span class="font-weight-bold">Skills - </span><span class="key-icon mr-2 d-inline-flex" style="background-color: %r1%"></span><span class="font-weight-semibold"> %r2%: %r3% </span>'), color, status, progress), true);
  },
  get overallProgress() {
    return wrapUnsafeHtml(i18n.__('<tt-I+LXiYbt>', 'Overall Progress'));
  },
  get studentNotPlayed() {
    return wrapUnsafeHtml(i18n.__('<tt-tJSDhcp8>', 'This student currently does not have any skills in progress.'));
  },
  matchingSkillsWithProgressOverall(progress) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-nb4oTiZV>', 'Matching skills/concepts with progress: %r1%'), progress));
  },
  skillsLearned(progress, total) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-uQA3Yss9>', 'Skills: %r1%/%r2% Learned'), progress, total));
  }
};

const parentDashboardLabels = {
  getRecommendedActivity(count) {
    return wrapUnsafeHtml(
      i18n._r(
        i18n._c('<tt-uW2+UJoL>', '%r1% %c1%',
          {'1': 'Recommended Activity', '%other%': 'Recommended Activities'},
          count
        ),
        count,
      ));
  },
  timeLeftToTarget(time) {
    return wrapUnsafeHtml(
      i18n._r(
        i18n._c('<tt-4Baoj8r4>', '%r1% %c1% Left to Target',
          {'1': 'Minute', '%other%': 'Minutes'},
          time
        ),
        time,
      ));
  },
  get PREVIOUS_LEARNED() {
    return wrapUnsafeHtml(i18n.__('<tt-kyf1jI1u>', 'Previously Learned'));
  },
  get FUTURE_SKILLS() {
    return wrapUnsafeHtml(i18n.__('<tt-SE38BPG2>', 'Future Skills'));
  },
  async skillsAndConceptsCompletedByStatus(color, status, progress) {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-k4Ve/Oo5>', '<span class="font-weight-bold">Skills - </span><span class="key-icon mr-2 d-inline-flex" style="background-color: %r1%"></span><span class="font-weight-semibold"> %r2%: %r3% </span>'), color, await status, progress), true);
  },
};

const gradesLabels = {
  get GRADE() {
    return (async () => unsafeHTML(await i18n.__('<tt-bLDN7Aom>', 'Grade:')))();
  },
  get [grades.PRE_K]() {
    return wrapUnsafeHtml(i18n.__('<tt-oZ7i7LBe>', 'Pre-K'));
  },
  get [grades.PREKINDERGARTEN]() {
    return wrapUnsafeHtml(i18n.__('<tt-tOWCAnL/>', 'Pre-K/Preschool/TK'));
  },
  get [grades.KINDERGARTEN]() {
    return wrapUnsafeHtml(i18n.__('<tt-x54kDEKG>', 'Kindergarten'));
  },
  get [grades.GRADE_1]() {
    return wrapUnsafeHtml(i18n.__('<tt-xa4tPDUt>', '1st Grade'), true);
  },
  get [grades.GRADE_2]() {
    return wrapUnsafeHtml(i18n.__('<tt-3QY0MpOV>', '2nd Grade'));
  },
  get [grades.GRADE_3]() {
    return wrapUnsafeHtml(i18n.__('<tt-fKErB9xL>', '3rd Grade'));
  },
  get [grades.GRADE_4]() {
    return wrapUnsafeHtml(i18n.__('<tt-fLjIIcyq>', '4th Grade'));
  },
  get [grades.GRADE_5]() {
    return wrapUnsafeHtml(i18n.__('<tt-y8R9Xc7J>', '5th Grade'));
  },
  get [grades.GRADE_6]() {
    return wrapUnsafeHtml(i18n.__('<tt-umkwwkaG>', '6th Grade'));
  },
  get [grades.GRADE_7]() {
    return wrapUnsafeHtml(i18n.__('<tt-Qn+yy7Vw>', '7th Grade'));
  },
  get [grades.GRADE_8]() {
    return wrapUnsafeHtml(i18n.__('<tt-hJ4cS7kv>', '8th Grade'));
  },
  get [grades.GRADE_9]() {
    return wrapUnsafeHtml(i18n.__('<tt-id24CISc>', '9th Grade'));
  },
  get [grades.GRADE_10]() {
    return wrapUnsafeHtml(i18n.__('<tt-lry6fqKw>', '10th Grade'));
  },
  get [grades.GRADE_11]() {
    return wrapUnsafeHtml(i18n.__('<tt-155PQZ54>', '11th Grade'));
  },
  get [grades.GRADE_12]() {
    return wrapUnsafeHtml(i18n.__('<tt-qrcTE181>', '12th Grade'));
  },
  get [grades.NOT_SET]() {
    return wrapUnsafeHtml(i18n.__('<tt-qp/4L64V>', 'Not Set'));
  },
  get [grades.OTHER]() {
    return wrapUnsafeHtml(i18n.__('<tt-8KvrHfrG>', 'Other'));
  },
  get first() {
    return (async () => unsafeHTML(await i18n.__('<tt-oDW6Bxap>', '1st')))();
  },
  get second() {
    return (async () => unsafeHTML(await i18n.__('<tt-oLPqUDPE>', '2nd')))();
  },
  get ALL_GRADES() {
    return wrapUnsafeHtml(i18n.__('<tt-53HsqxlU>', 'All Grades'));
  },
  dynamicGrade(grade) {
    switch (grade) {
      case 'Toddlertime':
        return gradesLabels[grades.TODDLERTIME];
      case 'Preschool':
        return gradesLabels[grades.PRESCHOOL];
      case 'Prekindergarten':
        return gradesLabels[grades.PREKINDERGARTEN];
      case 'Pre-K':
        return gradesLabels[grades.PREKINDERGARTEN];
      case 'Kindergarten':
        return gradesLabels[grades.KINDERGARTEN];
      case '1st Grade':
        return gradesLabels[grades.GRADE_1];
      case '2nd Grade':
        return gradesLabels[grades.GRADE_2];
      case '3rd Grade':
        return gradesLabels[grades.GRADE_3];
      case '4th Grade':
        return gradesLabels[grades.GRADE_4];
      case '5th Grade':
        return gradesLabels[grades.GRADE_5];
      case 'Other':
        return gradesLabels[grades.OTHER];
      default:
        return grade;
    }
  }
};

const jobStatusLabels = {
  get [jobStatuses.CANCELED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-+RePJNlJ>', 'Canceled')))();
  },
  get [jobStatuses.FAILED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-EUrPzi2/>', 'Failed')))();
  },
  get [jobStatuses.ACTIVE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-HPsV3Q3d>', 'Active')))();
  },
  get [jobStatuses.DONE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-Ooa/XXDC>', 'Done')))();
  },
  get [jobStatuses.PENDING]() {
    return (async () => unsafeHTML(await i18n.__('<tt-pZfzx0r5>', 'Pending User Action')))();
  },
  get [jobStatuses.QUEUED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-ObHpG2+7>', 'Queued')))();
  }
};

const jobStatusDescriptions = {
  get [jobStatuses.FAILED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-u9QT46Dy>', 'Licensing Job failed. Job will automatically run again to retry remaining licenses. Please check again later')))();
  }
};

const jobTypesLabels = {
  get [jobTypes.ROSTER_FULL]() {
    return (async () => unsafeHTML(await i18n.__('<tt-NN4r8JA4>', 'Rostering', 'Job type label. Used for job status heading')))();
  },
  get [jobTypes.EXPORT_SEARCH]() {
    return (async () => unsafeHTML(await i18n.__('<tt-xhd4KKn6>', 'Export Search Results', 'Job type label. Used for job status heading')))();
  },
  get [jobTypes.LICENSING]() {
    return (async () => unsafeHTML(await i18n.__('<tt-oa61qbzg>', 'License job')))();
  },
  get [jobTypes.INVITES]() {
    return (async () => unsafeHTML(await i18n.__('<tt-o04EmFgC>', 'Student Invitations')))();
  },
  get [jobTypes.INVITES_PRINT]() {
    return (async () => unsafeHTML(await i18n.__('<tt-aXG5f1Ua>', 'Student Invitations')))();
  },
  get [jobTypes.TEACHER_INVITES]() {
    return (async () => unsafeHTML(await i18n.__('<tt-FnR3RBaN>', 'Teacher Invitations')))();
  },
  get [jobTypes.ADMIN_INVITES]() {
    return (async () => unsafeHTML(await i18n.__('<tt-0QGVMfRO>', 'Admin Invitations')))();
  },
  get [jobTypes.ROSTER_NEW]() {
    return (async () => unsafeHTML(await i18n.__('<tt-NxJ3FmXn>', 'Rostering')))();
  },
  get [jobTypes.ROSTER_EXPORT]() {
    return (async () => unsafeHTML(await i18n.__('<tt-ZaquNHKp>', 'Roster Export')))();
  },
  get [jobTypes.BULK_REVOKE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-IgqGnBGE>', 'Bulk Revoke')))();
  },
  get [jobTypes.DASHBOARD_CSV_EXPORT]() {
    return (async () => unsafeHTML(await i18n.__('<tt-WJ5uNiph>', 'Dashboard CSV Export')))();
  },
  get [jobTypes.GENERATE_QR]() {
    return (async () => unsafeHTML(await i18n.__('<tt-LdWu3dIw>', 'Login QR Codes')))();
  },
};

const rosterStagesLabels = {
  get [rosterJobStages.ROSTER_PROCESSING_VALIDATE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-2t1HIdQW>', 'Validating Roster')))();
  },
  get [rosterJobStages.ROSTER_PROCESSING_COMMIT]() {
    return (async () => unsafeHTML(await i18n.__('<tt-kf+9uYXC>', 'Committing Roster')))();
  },
  get [rosterJobStages.ROSTER_VALIDATE_DONE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-O7JJSMeu>', 'Roster Validation Complete')))();
  },
  get [rosterJobStages.ROSTER_VALIDATE_ERRORS]() {
    return (async () => unsafeHTML(await i18n.__('<tt-iRHy0G8G>', 'Roster Validation Failed')))();
  },
  get [rosterJobStages.EMPTY]() {
    return Promise.resolve('');
  },
};

const exportStagesLabels = {
  get [exportJobStages.EXPORT_BEGIN]() {
    return (async () => unsafeHTML(await i18n.__('<tt-2R9Pk9K7>', 'Beginning Export')))();
  },
  get [exportJobStages.EXPORT_COMPLETE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-aKxeG7GD>', 'Export Complete')))();
  },
  get [exportJobStages.EMPTY]() {
    return Promise.resolve('');
  }
};

const platformInviteStageLabels = {
  get [inviteJobStages.INVITE_BEGIN]() {
    return (async () => unsafeHTML(await i18n.__('<tt-V/Byn1G7>', 'sending invites')))();
  },
  get [inviteJobStages.INVITE_COMPLETE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-7iX097We>', 'invites sent')))();
  },
};

const qrCodes = {
  get DOWNLOAD_QR_CODES() {
    return i18n.__('<tt-diC+vrE8>', 'Download QR Codes for Login');
  },
  get GENERATE_QR_FOR_LOGIN() {
    return i18n.__('<tt-R0z3IWlT>', 'Generate QR for Login');
  },
  get DOWNLOAD_PDF() {
    return i18n.__('<tt-GpuXfloH>', 'Download PDF');
  }
};

const jobStagesLabels = {
  [jobTypes.ROSTER_FULL]: rosterStagesLabels,
  [jobTypes.ROSTER_NEW]: rosterStagesLabels,
  [jobTypes.EXPORT_SEARCH]: exportStagesLabels,
  [jobTypes.ROSTER_EXPORT]: exportStagesLabels,
  [jobTypes.DASHBOARD_CSV_EXPORT]: exportStagesLabels,
  [jobTypes.LICENSING]: {
    get [rosterJobStages.EXTERNAL_ACTIONS_START]() {
      return (async () => unsafeHTML(await i18n.__('<tt-5I1sTu1i>', 'Licensing...')))();
    },
    get [rosterJobStages.EXTERNAL_ACTIONS_START_DONE]() {
      return (async () => unsafeHTML(await i18n.__('<tt-2rIK/7Ni>', 'Licensing')))();
    },
  },
  [jobTypes.INVITES]: {
    get [inviteJobStages.INVITE_BEGIN]() {
      return (async () => unsafeHTML(await i18n.__('<tt-FGTm7pPS>', 'sending invites')))();
    },
    get [inviteJobStages.INVITE_COMPLETE]() {
      return (async () => unsafeHTML(await i18n.__('<tt-Fhoa7FmH>', 'sending invites')))();
    },
  },
  [jobTypes.TEACHER_INVITES]: platformInviteStageLabels,
  [jobTypes.ADMIN_INVITES]: platformInviteStageLabels,
  [jobTypes.INVITES_PRINT]: {
    get [inviteJobStages.PRINT_INVITE_BEGIN]() {
      return (async () => unsafeHTML(await i18n.__('<tt-EOhjFmui>', 'preparing invites')))();
    },
    get [inviteJobStages.PRINT_INVITE_COMPLETE]() {
      return (async () => unsafeHTML(await i18n.__('<tt-6Q/CsOpF>', 'preparing invites')))();
    },
  },
  [jobTypes.BULK_REVOKE]: {
    get [bulkRevokeStages.REVOKE_ACTIONS_START]() {
      return (async () => unsafeHTML(await i18n.__('<tt-iSGDfhNR>', 'bulk revoke begin')))();
    },
    get [bulkRevokeStages.REVOKE_ACTIONS_QUEUED]() {
      return (async () => unsafeHTML(await i18n.__('<tt-MgbxIfjN>', 'bulk revoke actions queued')))();
    },
  },
  [jobTypes.GENERATE_QR]: {
    get [qrJobStages.GENERATE_QR_BEGIN]() {
      return (async () => unsafeHTML(await i18n.__('<tt-0s3LPgJK>', 'generating login QR codes')))();
    },
    get [qrJobStages.GENERATE_QR_COMPLETE]() {
      return (async () => unsafeHTML(await i18n.__('<tt-sqoNMv5G>', 'generating login QR codes')))();
    },
  },
  [jobTypes.PICTURE_PASSCODE_RESET]: {
    get [picturePasscodeResetJobStages.PICTURE_PASSCODE_RESET_BEGIN]() {
      return (async () => unsafeHTML(await i18n.__('<tt-ZV8FYZ4E>', 'picture passcode reset')))();
    },
    get [picturePasscodeResetJobStages.PICTURE_PASSCODE_RESET_COMPLETE]() {
      return (async () => unsafeHTML(await i18n.__('<tt-CkhYEjzf>', 'picture passcode reset')))();
    },
  },
};

const rosterErrorLabels = {
  get [rosterErrors.GENERIC]() {
    return (async () => unsafeHTML(await i18n.__('<tt-7n+p4Cph>', 'Generic')))();
  },
  get [rosterErrors.FILE_NOT_FOUND]() {
    return (async () => unsafeHTML(await i18n.__('<tt-6o7bIhhm>', 'File Not Found')))();
  },
  get [rosterErrors.AMBIGUOUS]() {
    return (async () => unsafeHTML(await i18n.__('<tt-RYokoLTj>', 'Multiple matched records for:')))();
  },
  get [rosterErrors.CSV_INVALID_PROPERTY]() {
    return (async () => unsafeHTML(await i18n.__('<tt-sDxY4I7o>', 'Incorrect Column Name')))();
  },
  get [rosterErrors.CSV_INVALID_COLUMN_COUNT]() {
    return (async () => unsafeHTML(await i18n.__('<tt-2OSmFV/Q>', 'Incorrect Column Count')))();
  },
  get [rosterErrors.CSV_MISALIGNED_GROUPING]() {
    return (async () => unsafeHTML(await i18n.__('<tt-euBgY+Du>', 'Misaligned Columns')))();
  },
  get [rosterErrors.DUPLICATE_EMAIL]() {
    return (async () => unsafeHTML(await i18n.__('<tt-8pOs5QJY>', 'Email Already In Use')))();
  },
  get [rosterErrors.DUPLICATE_PHONE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-KcvkfNnh>', 'Phone Number Already In Use')))();
  },
  get [rosterErrors.EMAIL_IN_USE_BY_ADMIN]() {
    return (async () => unsafeHTML(await i18n.__('<tt-Aj9sJ10s>', 'This email is already being used by an admin. A user cannot be an admin and teacher in the same organization.')))();
  },
  get [rosterErrors.INVALID_EMAIL]() {
    return (async () => unsafeHTML(await i18n.__('<tt-/r2xZ5oH>', 'Invalid Email Address')))();
  },
  get [rosterErrors.INVALID_NAME]() {
    return (async () => unsafeHTML(await i18n.__('<tt-V719aEjl>', 'Invalid characters: allowed characters are letters, spaces, periods, dashes, and commas')))();
  },
  get [rosterErrors.INVALID_COUNTRY_CODE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-q8V8uJ+2>', 'Invalid Country Code')))();
  },
  get [rosterErrors.INVALID_EXTERNAL_ID_LENGTH]() {
    return (async () => unsafeHTML(await i18n.__('<tt-8ZtSV6YD>', 'Unique Roster ID needs to be 60 characters or less')))();
  },
  get [rosterErrors.INVALID_LANGUAGE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-oeuPNTbv>', 'Invalid Language')))();
  },
  get [rosterErrors.INVALID_PHONE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-miv3etXu>', 'Invalid Phone Number Update to a valid 10-14 digit format, including area and country code.')))();
  },
  get [rosterErrors.INVALID_SWAP_OF_GROUP_LEVEL]() {
    return (async () => unsafeHTML(await i18n.__('<tt-c8RxE3ah>', 'This ID has already been used at another level (Region, District, School, or Class) ')))();
  },
  get [rosterErrors.INVALID_YEAR]() {
    return (async () => unsafeHTML(await i18n.__('<tt-KTMZgnkA>', 'Invalid Year')))();
  },
  get [rosterErrors.INVALID_MONTH]() {
    return (async () => unsafeHTML(await i18n.__('<tt-KPO7H3Qs>', 'Invalid Month')))();
  },
  get [rosterErrors.INVALID_TIERS]() {
    return (async () => unsafeHTML(await i18n.__('<tt-qw4btzpw>', 'Invalid roster format')))();
  },
  get [rosterErrors.ID_MISMATCH]() {
    return (async () => unsafeHTML(await i18n.__('<tt-r+JEAMJ5>', 'This name/email conflicts with an existing name/email in the system with the same Group/Teacher ID.')))();
  },
  get [rosterErrors.ID_REUSE_MISMATCH]() {
    return (async () => unsafeHTML(await i18n.__('<tt-u6HsdsUL>', 'This name-id pair conflicts with another name-id pair from this import.')))();
  },
  get [rosterErrors.NO_EMAIL_OR_PHONE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-2zlOu8s3>', 'Teacher Email or Teacher Phone is required')))();
  },
  get [rosterErrors.REPLACEMENT_ID_MISMATCH]() {
    return (async () => unsafeHTML(await i18n.__('<tt-aEp4SIq5>', 'Your file contains the same Replacement ID for two or more unique records.')))();
  },
  get [rosterErrors.REPLACEMENT_ID_USED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-6N2yw+5l>', 'A Replacement ID in your file conflicts with the Roster ID of an existing record.')))();
  },
  get [rosterErrors.REPLACEMENT_ID_ARCHIVED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-FDTF4rHG>', 'Replacement ID conflicts with the ID of an inactive/archived record.')))();
  },
  get [rosterErrors.REQUIRED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-Dr7siq1m>', 'This column is required')))();
  },
  get [rosterErrors.INVALID_REFERENCE]() {
    return (async () => unsafeHTML(await i18n.__('<tt-VA7Md5Wo>', 'Reference not found')))();
  },
};

const inviteResultLabels = {
  get usersAlreadyActive() {
    return (() => i18n.__('<tt-ncuJKR6B>', 'Already active, no invites needed'))();
  },
  get invalidEmails() {
    return (() => i18n.__('<tt-JOHts5PF>', 'Invalid emails'))();
  },
  get sent() {
    return (() => i18n.__('<tt-5dLAnIDO>', 'Invites sent'))();
  },
  get skipped() {
    return (() => i18n.__('<tt-eRAWxG2m>', 'Skipped (no contact info)'))();
  },
  get skipped_due_to_test_email() {
    return (() => i18n.__('<tt-hRIgagyC>', 'Skipped because of test email address'))();
  },
  productInvitesSent(product) {
    return (() => i18n._r(i18n.__('<tt-eXAmzpb2>', '%r1% invites sent'), product))();
  },
};

const reportLabels = {
  get home() {
    return (() => i18n.__('<tt-Ja+THci3>', 'Home'))();
  },
  get school() {
    return (() => i18n.__('<tt-OQXf5n22>', 'School'))();
  },
  get books() {
    return (() => i18n.__('<tt-NdyvFXJv>', 'Books'))();
  }
};

const summaryLabels = {
  get students() {
    return (async () => unsafeHTML(await i18n.__('<tt-3y+sWaO8>', 'Students')))();
  },
  get teachers() {
    return (async () => unsafeHTML(await i18n.__('<tt-lptrhxFp>', 'Teachers')))();
  },
  get licenses() {
    return (async () => unsafeHTML(await i18n.__('<tt-rK2L0sVK>', 'Licenses')))();
  },
  get classrooms() {
    return (async () => unsafeHTML(await i18n.__('<tt-aU5ALpuS>', 'Classrooms')))();
  }
};

const accessibleProductLabels = {
  get [productConst.ABCMOUSE]() {
    return unsafeHTML('ABC&ZeroWidthSpace;mouse');
  },
  get [productConst.READINGIQ]() {
    return unsafeHTML('Reading IQ');
  },
  get [productConst.MATH_ACADEMY]() {
    return unsafeHTML('My Math Academy');
  },
  get [productConst.MY_MATH_ACADEMY]() {
    return unsafeHTML('My Math Academy');
  },
  get [productConst.READING_MASTERY]() {
    return unsafeHTML('My Reading Academy');
  },
  get [productConst.MY_READING_ACADEMY]() {
    return unsafeHTML('My Reading Academy');
  },
  get [productConst.MY_READING_ACADEMY_ES]() {
    return unsafeHTML('My Reading Academy Español');
  },
  get [productConst.ABCMOUSE_ENGLISH]() {
    return unsafeHTML('ABC&ZeroWidthSpace;mouse Aprende Inglés');
  },
};

const accessibleAriaProductLabels = {
  [productConst.ABCMOUSE]: 'ABC​mouse',
  [productConst.READINGIQ]: 'Reading IQ',
  [productConst.MATH_ACADEMY]: 'My Math Academy',
  [productConst.MY_MATH_ACADEMY]: 'My Math Academy',
  [productConst.READING_MASTERY]: 'My Reading Academy',
  [productConst.MY_READING_ACADEMY]: 'My Reading Academy',
  [productConst.ABCMOUSE_ENGLISH]: 'ABC​mouse Aprende Inglés',
};

const products = {
  get [productConst.ABCMOUSE]() {
    return wrapUnsafeHtml(i18n.__('<tt-HStScPIc>', 'ABCmouse'));
  },
  get [productConst.READINGIQ]() {
    return wrapUnsafeHtml(i18n.__('<tt-t1vom1On>', 'Reading IQ'));
  },
  get [productConst.MATH_ACADEMY]() {
    return wrapUnsafeHtml(i18n.__('<tt-vQDMFewr>', 'My Math Academy'));
  },
  get [productConst.MY_MATH_ACADEMY]() {
    return wrapUnsafeHtml(i18n.__('<tt-PQcOqM7b>', 'My Math Academy'));
  },
  get [productConst.READING_MASTERY]() {
    return wrapUnsafeHtml(i18n.__('<tt-AYopdjBM>', 'My Reading Academy'));
  },
  get [productConst.MY_READING_ACADEMY]() {
    return wrapUnsafeHtml(i18n.__('<tt-aS2QBn0u>', 'My Reading Academy'));
  },
  get [productConst.ADVENTURE_ACADEMY]() {
    return wrapUnsafeHtml(i18n.__('<tt-6X5eyK1x>', 'Adventure Academy'));
  },
  get [productConst.ABCMOUSE_ENGLISH]() {
    return wrapUnsafeHtml(i18n.__('<tt-g/97VJnJ>', 'ABCmouse Aprende Inglés'));
  },
  get [productConst.MY_READING_ACADEMY_ES]() {
    return wrapUnsafeHtml(i18n.__('<tt-x/XIrn+H>', 'My Reading Academy Español'));
  }
};

const toastStatus = {
  recordDeleted: (label) => {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-jI26/g0B>', '%r1% successfully deleted'), label));
  },
  recordArchived: (label) => {
    return wrapUnsafeHtml(i18n._r(i18n.__('<tt-Wg5JPMWJ>', '%r1% successfully archived'), label));
  },
  get adminCreated() {
    return (async () => unsafeHTML(await i18n.__('<tt-NLVjmv7Y>', 'Administrator created successfully. Please add a role to the administrator.')))();
  },
  get pleaseAddRole() {
    return (async () => unsafeHTML(await i18n.__('<tt-nm/iKjZV>', 'Please add a role to the administrator.')))();
  },
  get studentCreated() {
    return (async () => unsafeHTML(await i18n.__('<tt-T+FAFSZT>', 'Student created successfully!  Remember to add them to a class and assign them a  license so they can begin using the product.')))();
  },
  get saveSuccess() {
    return (async () => unsafeHTML(await i18n.__('<tt-HtJVnvgt>', 'Changes saved successfully.')))();
  },
  get saveFail() {
    return (async () => unsafeHTML(await i18n.__('<tt-3QuGKJw1>', 'Oops! Something went wrong.')))();
  },
  get oops() {
    return (async () => unsafeHTML(await i18n.__('<tt-6Taob0zz>', 'Oops! Something went wrong.')))();
  },
  get saveFailTpRosteringConnected() {
    return (async () => unsafeHTML(await i18n.__('<tt-azXWL4h0>', 'Your organization is currently synced with a 3rd party rostering system.<br>While connected, any changes to rosters, except to student language, must be done there.')))();
  },
  get passcodeReset() {
    return (async () => unsafeHTML(await i18n.__('<tt-DbOvGgNH>', 'You have successfully reset this student\'s picture password. When the student selects a new one, you will see their new selection here.')))();
  },
  get passcodeResetTitle() {
    return (async () => unsafeHTML(await i18n.__('<tt-rc0ZMGzm>', 'Student\'s password has been reset')))();
  },
  get teacherInvitesQueued() {
    return (async () => unsafeHTML(await i18n.__('<tt-C9eEA8rQ>', 'Teacher invites have been queued.')))();
  },
  get adminInvitesQueued() {
    return (async () => unsafeHTML(await i18n.__('<tt-TUh+90XG>', 'Admin invites have been queued.')))();
  },
  get selfConflictingProductCredentials() {
    return (async () => unsafeHTML(await i18n.__('<tt-HQ0s/jyq>', 'There is a problem changing your email in the Age of Learning products you have access to. Please contact your admin.')))();
  },
  get adminConflictingProductCredentials() {
    return (async () => unsafeHTML(await i18n.__('<tt-xHsTGj+i>', 'There is a problem changing this email in the Age of Learning products the teacher has access to. Please contact your admin.')))();
  },
  get duplicateRole() {
    return (async () => unsafeHTML(await i18n.__('<tt-kZfQveCL>', 'This admin already has this role. If you need to make updates, you may delete the existing one and add a new one with updated permissions.')))();
  },
  get duplicateEmail() {
    return (async () => unsafeHTML(await i18n.__('<tt-PhSysTnR>', 'This email is already in use.')))();
  },
  get duplicateRestoreLoginCredentials() {
    return (async () => unsafeHTML(await i18n.__('<tt-it3jcL/A>', 'Login credentials already in use.')))();
  },
  get duplicatePhone() {
    return (async () => unsafeHTML(await i18n.__('<tt-uIIVB0wX>', 'This phone number is already in use.')))();
  },
  get productLoginCredentialsSuccess() {
    return (async () => unsafeHTML(await i18n.__('<tt-aWcqN2iP>', 'Your login credentials were successfully updated. Please note this update applies to this platform as well as your credentials for any licensed products you have access to via this organization.')))();
  },
  get passwordResetFailureUnauthorized() {
    return (async () => unsafeHTML(await i18n.__('<tt-zYCitwEW>', 'This account is not authorized to reset their password.')))();
  },
  get passwordResetUserNotFound() {
    return (async () => unsafeHTML(await i18n.__('<tt-WoYzbJrh>', 'That account was not found in our system.')))();
  },
  get passwordResetFailure() {
    return (async () => unsafeHTML(await i18n.__('<tt-HoqE3YlX>', 'Your password could not be reset due to an error.')))();
  },
  get insufficentPermissionsEdit() {
    return (async () => unsafeHTML(await i18n.__('<tt-uNyhvl/Y>', 'This account is not authorized to make edits.')))();
  },
  get studentAlreadyLicensed() {
    return (async () => unsafeHTML(await i18n.__('<tt-Vxk9nixR>', 'Student already has this license.')))();
  },
  get orgNameDuplicate() {
    return (async () => unsafeHTML(await i18n.__('<tt-ooPGhqfk>', 'Cannot have duplicate name.')))();
  },
  get invalidExternalIdLength() {
    return (async () => unsafeHTML(await i18n.__('<tt-AT/uRnhX>', 'Unique Roster ID needs to be 60 characters or less')))();
  },
  get externalIdDuplicated() {
    return (async () => unsafeHTML(await i18n.__('<tt-dLcYeMds>', 'External ID is already in use')))();
  },
  get userTypeErrorExternalId() {
    return (async () => unsafeHTML(await i18n.__('<tt-odNfkTCR>', 'This external id address is already in use by another account type')))();
  },
  get userTypeErrorEmail() {
    return (async () => unsafeHTML(await i18n.__('<tt-Lj4ce264>', 'This email address is already in use by another account type')))();
  },
  get userTypeErrorPhone() {
    return (async () => unsafeHTML(await i18n.__('<tt-DkVRp8HX>', 'This phone number is already in use by another account type')))();
  },
  get roster_upload_permission_required_with_sftp() {
    return (async () => unsafeHTML(await i18n.__('<tt-uwhAvxJ8>', 'Roster Upload permission is required with Manage SFTP permission.')))();
  },
  get cannotSortInactiveGroups() {
    return (async () => unsafeHTML(await i18n.__('<tt-uXWntkV0>', 'It is not possible to sort all groups this week due to inactivity.')))();
  },
  down_for_maintenance: (timestamp) => {
    return (async () => unsafeHTML(await i18n._r(i18n.__('<tt-6Pl6B1NN>', 'This website will be down for maintenance at %r1%'), new Date(timestamp * 1000).toLocaleString(window.navigator.language, {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'}))));
  },
  reportDownloaded: (count, reportType) => {
    return wrapUnsafeHtml(i18n._r(i18n._c('<tt-sJZx3Nl3>', '%r1% %c1% been downloaded', {'1': 'Report has', '%other%': 'Reports have'}, count), printReportLabels[reportType]));
  },
  get downloadStarted() {
    return (async () => unsafeHTML(await i18n.__('<tt-fiS+F3Kj>', 'Download started')))();
  },
  get orgLocked() {
    return (async () => unsafeHTML(await i18n.__('<tt-oLGxyPqU>', 'Your organization is currently locked. Please contact <a class="text-link-primary" href="https://support.aofl.com/hc/en-us" target="_blank">Age of Learning Support</a> for assistance in resolving this issue.')))();
  },
};

const productStatus = {
  get [productStatuses.UNASSIGNED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-s+gEoktg>', '')))();
  },
  get [productStatuses.ASSIGNED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-/feIiSzx>', 'Assigned')))();
  },
  get [productStatuses.INVITE_SENT]() {
    return (async () => unsafeHTML(await i18n.__('<tt-wKSMkNqR>', 'Invited')))();
  },
  get [productStatuses.REDEEMED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-OvrTiObe>', 'Redeemed')))();
  },
  get [productStatuses.EXPIRED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-kK4uwdGZ>', 'Expired')))();
  },
  get [productStatuses.EXPIRING_SOON]() {
    return (async () => unsafeHTML(await i18n.__('<tt-5dcanqJk>', 'Expiring Soon (2 Wks)')))();
  },
  get [productStatuses.ASSIGNED_INVITED_REDEEMED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-gNa+cgIX>', 'Assigned + Invited + Redeemed')))();
  },
  get [productStatuses.INVALID_EMAIL]() {
    return (async () => unsafeHTML(await i18n.__('<tt-kQqOukuG>', 'Invalid Email')))();
  },
};

const userEmsAccessStatus = {
  get [teacherAccessStatuses.NOT_INVITED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-dSjD6kVi>', 'Not Invited')))();
  },
  get [teacherAccessStatuses.INVITED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-pEASVT50>', 'Invited')))();
  },
  get [teacherAccessStatuses.ACTIVATED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-xAdaj4oQ>', 'Activated')))();
  },
  get [teacherAccessStatuses.INVALID_EMAIL]() {
    return (async () => unsafeHTML(await i18n.__('<tt-OGjEG5q9>', 'Invalid Email')))();
  },
};

const teacherProductStatus = {
  get [productStatuses.UNASSIGNED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-bKehT0aA>', '', '')))();
  },
  get [productStatuses.ASSIGNED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-edzdPt5s>', 'Not Redeemed')))();
  },
  get [productStatuses.NOT_ACTIVATED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-sJl3wDKU>', 'Not Activated')))();
  },
  get [productStatuses.INVITE_SENT]() {
    return (async () => unsafeHTML(await i18n.__('<tt-KfYfU0sH>', 'Invited')))();
  },
  get [productStatuses.REDEEMED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-APbEFevH>', 'Redeemed')))();
  },
  get [productStatuses.NOT_REDEEMED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-FeK+35vD>', 'Not Redeemed')))();
  },
  get [productStatuses.ACTIVATED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-zXYH8Lgi>', 'Activated')))();
  },
  get [productStatuses.EXPIRED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-RYceI1LM>', 'Expired')))();
  },
  get [productStatuses.NOT_ACTIVATED_AND_ACTIVATED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-HSTL1mAF>', 'Activated + Not Activated')))();
  },
  get [productStatuses.NOT_REDEEMED_AND_REDEEMED]() {
    return (async () => unsafeHTML(await i18n.__('<tt-Un2JGQHP>', 'Redeemed + Not Redeemed')))();
  }
};

const actionItemsStatus = {
  get hitTargetTitle() {
    return (async () => unsafeHTML(await i18n.__('<tt-swfBV5O8>', 'Hit Target this week')))();
  },
  get belowTarget() {
    return (async () => unsafeHTML(await i18n.__('<tt-tbvVf2m/>', 'Below Target this week')))();
  },
  get inactiveTarget() {
    return (async () => unsafeHTML(await i18n.__('<tt-FX4RY7F1>', 'Inactive this week')))();
  },
  activityCountContent(activitiesCompletedCount, targetActivitiesCount) {
    return (async () => unsafeHTML(await i18n._r(i18n.__('<tt-bwQc6iUx>', '%r1% activities completed out of target: %r2%'), activitiesCompletedCount, targetActivitiesCount)))();
  },
};

const abcSubjectLabels = {
  get [abcSubjectBuckets.MATH]() {
    return wrapUnsafeHtml(i18n.__('<tt-qH6FZM4N>', 'Math'));
  },
  get [abcSubjectBuckets.ARTS_AND_SCIENCES]() {
    return wrapUnsafeHtml(i18n.__('<tt-OTtqxFQS>', 'Arts & Sciences'));
  },
  get [abcSubjectBuckets.FREE_PLAY]() {
    return wrapUnsafeHtml(i18n.__('<tt-DDTQls6q>', 'Free Play'));
  },
  get [abcSubjectBuckets.READING]() {
    return wrapUnsafeHtml(i18n.__('<tt-/uAs/B3U>', 'Reading'));
  },
};

const noSearchResults = (async () => unsafeHTML(await i18n.__('<tt-pywRYYm0>', 'No results. Modify your search criteria and try again.')))();

const addClasses = () => {
  return wrapUnsafeHtml(i18n.__('<tt-JFD2HQs0>', 'Add New Classes'));
};

const searchLabels = {
  get NO_MATCHING_RESULTS() {
    return (async () => unsafeHTML(await i18n.__('<tt-HH+AwSjy>', 'No Matching Results')))();
  },
  showingFirstOfAmount(amount) {
    return (async () => unsafeHTML(await i18n._r(i18n.__('<tt-Mz3JAwU+>', 'Showing first %r1% results. Search above for more.'), amount)))();
  }
};

const dropdownFilterLabels = {
  get productDropdown() {
    return (async () => unsafeHTML(await i18n.__('<tt-LyU4yv9t>', 'Select Product')))();
  },
  get licenseDropdown() {
    return (async () => unsafeHTML(await i18n.__('<tt-qLytdBUJ>', 'Select License')))();
  },
  get licenseStatusDropdown() {
    return (async () => unsafeHTML(await i18n.__('<tt-azCrEAsm>', 'Select License Status')))();
  },
  get platformAccessDropdown() {
    return (async () => unsafeHTML(await i18n.__('<tt-CG84Fen8>', 'Select AofL Platform Access')))();
  }
};

const generalLabels = {
  get Yes() {
    return (async () => unsafeHTML(await i18n.__('<tt-KzlWsBY6>', 'Yes')))();
  },
  get No() {
    return (async () => unsafeHTML(await i18n.__('<tt-q7wQx1yK>', 'No')))();
  },
  get View() {
    return (async () => unsafeHTML(await i18n.__('<tt-lJ0/6vib>', 'View')))();
  },
  get DurationLabel() {
    return (async () => unsafeHTML(await i18n.__('<tt-debshTdU>', 'Set Duration')))();
  },
  get FixedEndDateLabel() {
    return (async () => unsafeHTML(await i18n.__('<tt-+BvJZzOT>', 'Fixed End Date')))();
  },
  get FixedEndDateExpiredLabel() {
    return (async () => unsafeHTML(await i18n.__('<tt-Qc2UJuCz>', 'Fixed End Date (Expired)')))();
  },
  get TransferableLicense() {
    return (async () => unsafeHTML(await i18n.__('<tt-9/OQqfjq>', 'Transferable License')))();
  },
  get OpenInDashboardLabel() {
    return (async () => unsafeHTML(await i18n.__('<tt-u7Uy5fVz>', 'Open in Dashboard')))();
  },
  get createAnother() {
    return (async () => unsafeHTML(await i18n.__('<tt-exiIQQXu>', 'Create Another')))();
  },
  get bulkActions() {
    return (async () => unsafeHTML(await i18n.__('<tt-u9zZH31S>', 'Bulk Actions')))();
  },
  get loading() {
    return (async () => unsafeHTML(await i18n.__('<tt-FFhWLUqY>', 'Loading...')))();
  },
  get reset() {
    return (async () => unsafeHTML(await i18n.__('<tt-tvw76fIb>', 'Reset')))();
  },
  get applyFilters() {
    return (async () => unsafeHTML(await i18n.__('<tt-HL9uDxnc>', 'Apply Filters')))();
  },
  get showFilters() {
    return (async () => unsafeHTML(await i18n.__('<tt-BYYCz4d+>', 'Show Filters')))();
  },
  get hideFilters() {
    return (async () => unsafeHTML(await i18n.__('<tt-ff8JcreO>', 'Hide Filters')))();
  },
  get createStudent() {
    return (async () => unsafeHTML(await i18n.__('<tt-+uNGr7va>', 'Create Student', 'Create student link, ...')))();
  },
  get createTeacher() {
    return (async () => unsafeHTML(await i18n.__('<tt-R/H+S4GS>', 'Create Teacher', 'Create teacher link, ...')))();
  },
  get createClass() {
    return (async () => unsafeHTML(await i18n.__('<tt-vz2xYFF+>', 'Create Classes', 'Create Classroom, ...')))();
  },
  createOrg(tier) {
    return (async () => unsafeHTML(await i18n._r(i18n.__('<tt-iE2n7+6F>', 'Create %r1%', 'Create organization group button e.g. Create District, Create Region, ...'), tier)))();
  },
  get save() {
    return (async () => unsafeHTML(await i18n.__('<tt-5atyJwJk>', 'Save')))();
  },
  get cancel() {
    return (async () => unsafeHTML(await i18n.__('<tt-GsdmPIG8>', 'Cancel')))();
  },
  get back() {
    return wrapUnsafeHtml(i18n.__('<tt-XZENPypZ>', 'Back'));
  },
  get create() {
    return (async () => unsafeHTML(await i18n.__('<tt-8N3W7Rza>', 'Create')))();
  },
  get print() {
    return (async () => unsafeHTML(await i18n.__('<tt-usdazAPZ>', 'Print')))();
  },
  get printerFriendly() {
    return (async () => unsafeHTML(await i18n.__('<tt-G/e14mYF>', 'Printer Friendly')))();
  },
  confirmDeleteGroup(name) {
    return (async () => unsafeHTML(await i18n._r(i18n.__('<tt-pT9BsKA2>', 'Are you sure you want to delete %r1%? This will delete any levels under it, including classes. Teacher and student records will remain, but will be unassigned from all classes under %r1%.'), name)))();
  },
  confirmArchiveGroup(name) {
    return (async () => unsafeHTML(await i18n._r(i18n.__('<tt-bB6LfQvE>', 'Are you sure you want to archive %r1%? This will archive any levels under it, including classes. Teacher and student records will remain, but will be unassigned from all classes under %r1%.'), name)))();
  },
  get createAdmin() {
    return (async () => unsafeHTML(await i18n.__('<tt-zE9mtXBG>', 'Create Administrator')))();
  },
  get AofLPlatformAccess() {
    return wrapUnsafeHtml(i18n.__('<tt-nKdojLD4>', 'AofL Platform Access'));
  },
  get viewStudents() {
    return (async () => unsafeHTML(await i18n.__('<tt-HZzCmjIs>', 'View Students')))();
  },
  get nameInUse() {
    return (async () => unsafeHTML(await i18n.__('<tt-KBrrV2My>', 'Name already in use')))();
  },
  get invalidEmailError() {
    return (async () => unsafeHTML(await i18n.__('<tt-frjN6Dej>', 'Please provide a valid email address.')))();
  },
  get invalidPhoneError() {
    return (async () => unsafeHTML(await i18n.__('<tt-eGgDfjHD>', 'Update phone number to a valid 10-14 digit format, including area and country code.')))();
  },
  get externalIdEmptyRestored() {
    return (async () => unsafeHTML(await i18n.__('<tt-M81NK8ze>', 'External Id of an existing record cannot be blank, current ID restored.')))();
  },
  get externalIdInUseError() {
    return (async () => unsafeHTML(await i18n.__('<tt-QUZvtYet>', 'External id already in use')))();
  },
  get sisIdInUseError() {
    return (async () => unsafeHTML(await i18n.__('<tt-7po5Upx1>', 'SIS ID already in use')))();
  },
  get all() {
    return (async () => unsafeHTML(await i18n.__('<tt-qNR5VSg7>', 'All')))();
  },
  get loginEmail() {
    return (async () => unsafeHTML(await i18n.__('<tt-/i3XjZVs>', 'Login Email')))();
  },
  get contactEmail() {
    return (async () => unsafeHTML(await i18n.__('<tt-00zPZF2P>', 'Contact Email')))();
  },
  get phone() {
    return (async () => unsafeHTML(await i18n.__('<tt-HYaFHUso>', 'Phone')))();
  },
  get loginPhone() {
    return (async () => unsafeHTML(await i18n.__('<tt-0ZmXxGyY>', 'Login Phone')))();
  },
  get contactPhone() {
    return (async () => unsafeHTML(await i18n.__('<tt-l/G9tGCT>', 'Contact Phone')))();
  },
  get download() {
    return (async () => unsafeHTML(await i18n.__('<tt-W0tRuecb>', 'Download')))();
  },
  get select() {
    return (async () => unsafeHTML(await i18n.__('<tt-1ghShPyY>', 'Select')))();
  },
  get history() {
    return i18n.__('<tt-nufWwhbJ>', 'History');
  },
  get administrators() {
    return i18n.__('<tt-Xe480Hm+>', 'Administrators');
  },
  get viewGroups() {
    return i18n.__('<tt-NOI3Gxlw>', 'View Groups');
  },
  get groups() {
    return i18n.__('<tt-xbhmYxfo>', 'Groups');
  },
  get createGroup() {
    return i18n.__('<tt-5V+10978>', 'Create Group');
  }
};

const licenseLabels = {
  get licenseName() {
    return (async () => unsafeHTML(await i18n.__('<tt-N3/6/xBB>', 'License Name')))();
  },
  get licenseType() {
    return (async () => unsafeHTML(await i18n.__('<tt-co+ZAF0x>', 'License Type')))();
  },
  get homeAccessType() {
    return (async () => unsafeHTML(await i18n.__('<tt-G2VVf2ta>', 'Home Access Type')))();
  },
};

const bulkActionLabels = {
  get revokeLicenses() {
    return (async () => unsafeHTML(await i18n.__('<tt-3EL2Bpr/>', 'Revoke Licenses')))();
  },
};

const addressLabels = {
  get Zip() {
    return (async () => unsafeHTML(await i18n.__('<tt-6dFcfVPO>', 'Zip')))();
  },
  get State() {
    return (async () => unsafeHTML(await i18n.__('<tt-Dl4+m7Fv>', 'State')))();
  },
};

const formLabels = {
  get password() {
    return (async () => unsafeHTML(await i18n.__('<tt-M3+PqAWX>', 'Password')))();
  },
  get confirmPassword() {
    return (async () => unsafeHTML(await i18n.__('<tt-WzX6mQSm>', 'Confirm Password')))();
  },
  get showPasswords() {
    return (async () => unsafeHTML(await i18n.__('<tt-WQb0D6DW>', 'Show Password')))();
  },
  get required() {
    return (async () => unsafeHTML(await i18n.__('<tt-LJLKw33P>', 'Required')))();
  },
  get submit() {
    return (async () => unsafeHTML(await i18n.__('<tt-XILWDzOV>', 'Submit')))();
  },
  get requiredField() {
    return (async () => unsafeHTML(await i18n.__('<tt-Dv3CDI2B>', '*= required field')))();
  },
};

const generalAriaLabels = {
  get HAMBURGER_MENU() {
    return i18n.__('<tt-xnmGEV72>', 'Open Menu');
  },
  get SEARCH() {
    return i18n.__('<tt-ojE9vLxG>', 'Search');
  },
  currentLanguage(value) {
    return i18n._r(i18n.__('<tt-oJ+gvT3E>', 'Currently Selected Language:%r1%'), value);
  },
  createLabel(value) {
    return i18n._r(i18n.__('<tt-8tEqsE7/>', 'Create %r1%'), value);
  },
  viewLabel(value) {
    return i18n._r(i18n.__('<tt-scxRtwuh>', 'View %r1%'), value);
  },
  get STUDENTS() {
    return i18n.__('<tt-kqYiDsd9>', 'Students');
  },
  get LICENSES() {
    return i18n.__('<tt-mYsIMQ3R>', 'Licenses');
  },
  get LICENSE_INVENTORY() {
    return i18n.__('<tt-Gqn29GMB>', 'License Inventory');
  },
  get CLASSES() {
    return i18n.__('<tt-PYH0zAfL>', 'Classes');
  },
  get TEACHERS() {
    return i18n.__('<tt-2ztV3/uJ>', 'Teachers');
  },
  get NOTIFICATION_MENU() {
    return i18n.__('<tt-ZlBmw2Pc>', 'Notification Menu');
  },
};

const siteHeaderLabels = {
  get SWITCH_ROLE() {
    return wrapUnsafeHtml(i18n.__('<tt-gBb94XEt>', 'Switch Role'));
  },
  get LOG_OUT() {
    return wrapUnsafeHtml(i18n.__('<tt-jP8Lnx6c>', 'Log Out'));
  },
};

const studentFormLabels = {
  get BIRTH_MONTH() {
    return wrapUnsafeHtml(i18n.__('<tt-CdQV0lfJ>', 'Birth month'));
  },
  get BIRTH_YEAR() {
    return wrapUnsafeHtml(i18n.__('<tt-fGnpm0lz>', 'Birth year'));
  },
};

export {
  i18n,
  abcSubjectLabels,
  actionItemsStatus,
  addressLabels,
  addClasses,
  studentDashboardAriaLabels,
  dashboardAriaLabels,
  dashboardLabels,
  studentDashboardLabels,
  parentDashboardLabels,
  tierStructure,
  jobStatusLabels,
  jobTypesLabels,
  jobStagesLabels,
  leftMenuLabels,
  rosterErrorLabels,
  reportLabels,
  summaryLabels,
  products,
  productStatus,
  teacherProductStatus,
  toastStatus,
  jobStatusDescriptions,
  userEmsAccessStatus,
  noSearchResults,
  gradesLabels,
  inviteResultLabels,
  generalLabels,
  formLabels,
  searchLabels,
  dropdownFilterLabels,
  chartTitles,
  licenseLabels,
  bulkActionLabels,
  licenseHomeAccessTypeLabels,
  activationStartTypeLabels,
  conceptsMasteredColumnLabels,
  studentDashboardTimeSpentAriaLabels,
  studentDashboardMinutesRemainingAriaLabels,
  generalAriaLabels,
  accessibleProductLabels,
  accessibleAriaProductLabels,
  siteHeaderLabels,
  qrCodes,
  studentFormLabels
};
